import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Phone, 
  MessageCircle, 
  ChevronRight, 
  Target, 
  TrendingUp, 
  Users, 
  BarChart3, 
  Award,
  CheckCircle2,
  ArrowRight,
  X,
  Star,
  Globe,
  Zap,
  Shield,
  Clock,
  Mail,
  MapPin
} from "lucide-react";
import { SiMeta, SiGoogleads } from "react-icons/si";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_lead-autopilot-11/artifacts/0dp7tt4n_Founder.jpeg";
const GOOGLE_LOGO = "https://customer-assets.emergentagent.com/job_lead-autopilot-11/artifacts/h1h9lw3e_Google__G__logo.svg.webp";
const META_LOGO = "https://customer-assets.emergentagent.com/job_lead-autopilot-11/artifacts/3on0nmji_pngimg.com%20-%20meta_PNG5.png";
const GLOBEWICK_LOGO = "https://customer-assets.emergentagent.com/job_lead-autopilot-11/artifacts/l6ydboq3_GB%20WHITE%20LOGO%20%281%29.png";
const COMPANY_EMAIL = process.env.REACT_APP_COMPANY_EMAIL;
const COMPANY_LOCATION = process.env.REACT_APP_COMPANY_LOCATION;

// Process/Services data from reference image
const SERVICES_DATA = [
  {
    title: "SEO",
    description: "Outperform your biggest rivals with India's leading SEO Company. Generate incredible leads through the power of SEO.",
    items: ["Local SEO", "International SEO", "Ecommerce SEO", "Enterprise SEO"]
  },
  {
    title: "Google Ads",
    description: "Let our experts take charge of your PPC campaign. Don't Settle For LESS, Achieve BIG With Our Performance Marketing Strategies.",
    items: ["Google Ads", "PMax Campaigns", "Shopping Campaigns", "Planning & Strategies"]
  },
  {
    title: "Meta Ads",
    description: "From Generating leads from the businesses to Generating sales at better ROAS for your business. We exactly know how meta ecosystem works now.",
    items: ["Ads Creation & Brainstorming", "Facebook Ads", "Instagram Ads"]
  },
  {
    title: "Development",
    description: "We understand the Concept of CRO for Landing Pages. We know How to make a website SEO Friendly. Get our Web Development Services.",
    items: ["High Converting Landing Page", "SEO Friendly Websites", "Custom Coded Websites"]
  }
];

// Hero Section
const HeroSection = ({ scrollToForm }) => {
  return (
    <section 
      className="hero-bg min-h-screen flex items-center"
      style={{ 
        backgroundImage: `url('https://images.pexels.com/photos/31858728/pexels-photo-31858728.jpeg')` 
      }}
      data-testid="hero-section"
    >
      <div className="hero-content w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="trust-badge">
                <img src={GOOGLE_LOGO} alt="Google" className="w-5 h-5 object-contain" />
                <span>Google Partner</span>
              </div>
              <div className="trust-badge">
                <img src={META_LOGO} alt="Meta" className="w-5 h-5 object-contain" />
                <span>Meta Partner</span>
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-tight">
              Stop Relying on Referrals.{" "}
              <span className="neon-cyan">Get Daily Travel Leads</span>{" "}
              on Autopilot.
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
              We help travel agencies scale with high-converting ad campaigns that bring 
              <span className="text-[#FFD700] font-semibold"> ready-to-book customers</span>. 
              Generate consistent leads and scale to <span className="neon-gold font-bold">Rs.1Cr+ revenue</span>.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col  gap-4">
              <Button 
                onClick={scrollToForm}
                className="btn-primary px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-none w-full w-full"
                data-testid="hero-cta-strategy-call"
              >
                Book Free Strategy Call
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
             <Button 
                onClick={scrollToForm}
                className="btn-secondary px-6 sm:px-8 py-4 sm:py-4 text-base sm:text-lg rounded-none w-full text-center whitespace-normal  min-h-[68px] sm:min-h-[56px]"
                data-testid="hero-cta-get-leads"
              >
                Unlock 20+ Qualified Leads That Actually Convert
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl">
              <div>
                <div className="stat-number neon-cyan text-2xl sm:text-4xl lg:text-5xl">8+</div>
                <p className="text-slate-400 text-xs sm:text-sm">Years Experience</p>
              </div>
              <div>
                <div className="stat-number neon-gold text-2xl sm:text-4xl lg:text-5xl">50+</div>
                <p className="text-slate-400 text-xs sm:text-sm">Travel Agencies</p>
              </div>
              <div>
                <div className="stat-number text-white text-2xl sm:text-4xl lg:text-5xl">70Cr+</div>
                <p className="text-slate-400 text-xs sm:text-sm">Revenue Generated</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const problems = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Struggling to get consistent bookings?",
      description: "Your revenue fluctuates wildly month-to-month. Some months are great, others are painfully slow."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Tired of low-quality leads?",
      description: "You're getting inquiries, but they never convert. Time-wasters who are 'just browsing' waste your valuable time."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Running ads but not getting ROI?",
      description: "You've tried Facebook and Google ads, but the leads are expensive and rarely turn into actual bookings."
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-[#0A1325]" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#00E5FF] uppercase tracking-[0.2em] font-bold text-sm mb-4">
            Sound Familiar?
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            The <span className="neon-gold">Struggles</span> Every Travel Agency Faces
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="neon-card glass-card p-8 rounded-2xl border border-white/5"
              data-testid={`problem-card-${index}`}
            >
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-500/20 to-red-900/20 flex items-center justify-center mb-6 text-red-400">
                {problem.icon}
              </div>
              <h3 className="text-xl font-semibold mb-4">{problem.title}</h3>
              <p className="text-slate-400 leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section - Services Grid
const SolutionSection = () => {
  const serviceIcons = {
    "SEO": <Globe className="w-8 h-8" />,
    "Google Ads": <img src={GOOGLE_LOGO} alt="Google Ads" className="w-8 h-8 object-contain" />,
    "Meta Ads": <img src={META_LOGO} alt="Meta Ads" className="w-8 h-8 object-contain" />,
    "Development": <Zap className="w-8 h-8" />
  };

  const serviceColors = {
    "SEO": "text-[#00E5FF]",
    "Google Ads": "text-[#4285F4]",
    "Meta Ads": "text-[#0081FB]",
    "Development": "text-[#FFD700]"
  };

  return (
    <section className="py-20 lg:py-32 bg-[#030712]" data-testid="solution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Our ROI driven Services for both Ecommerce as well as{" "}
            <span className="neon-cyan">Lead Generation Businesses!</span>
          </h2>
          <p className="text-slate-400 text-lg">
            Honest, 100% transparency and Data Driven Approach is how we operate.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES_DATA.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass-card rounded-2xl p-6 border border-white/5 hover:border-[#00E5FF]/30 transition-all duration-300 group"
              data-testid={`service-card-${index}`}
            >
              <div className={`${serviceColors[service.title]} mb-4`}>
                {serviceIcons[service.title]}
              </div>
              <h3 className="text-xl font-bold mb-3">{service.title}</h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">{service.description}</p>
              
              <p className="text-white font-semibold mb-3">Start with...</p>
              <ul className="space-y-2">
                {service.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-[#00E5FF]" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="mt-6 text-[#00E5FF] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all"
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Partner Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto"
        >
          <div className="glass-card rounded-xl p-6 border border-white/10 hover:border-[#4285F4]/50 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[#4285F4] text-xs uppercase tracking-wider mb-1">Leading Google Partner</p>
                <h4 className="font-bold text-xl mb-2">Google Official Partners</h4>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                <img src={GOOGLE_LOGO} alt="Google" className="w-6 h-6 object-contain" />
                <span className="text-[#4285F4] font-bold text-sm">Partner</span>
              </div>
            </div>
            <p className="text-[#FFD700] font-semibold text-sm mb-3">WHAT ARE YOUR ADVANTAGES?</p>
            <p className="text-slate-400 text-sm mb-4">Our Google-certified, award-winning specialists will strategically plan and execute your campaigns for maximum ROI.</p>
            <button 
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-red-400 font-semibold text-sm tracking-wider hover:text-red-300 transition-colors"
            >
              Talk to Us
            </button>
          </div>
          <div className="glass-card rounded-xl p-6 border border-white/10 hover:border-[#0081FB]/50 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[#0081FB] text-xs uppercase tracking-wider mb-1">Leading Meta Business Partner</p>
                <h4 className="font-bold text-xl mb-2">Meta Official Partner</h4>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                <img src={META_LOGO} alt="Meta" className="w-6 h-6 object-contain" />
                <span className="text-[#0081FB] font-bold text-sm">Partner</span>
              </div>
            </div>
            <p className="text-[#FFD700] font-semibold text-sm mb-3">WHAT ARE YOUR ADVANTAGES?</p>
            <p className="text-slate-400 text-sm mb-4">Get your account in the hands of people who spend big and are recognized by Meta as official Business Partner.</p>
            <button 
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-red-400 font-semibold text-sm tracking-wider hover:text-red-300 transition-colors"
            >
              Talk to Us
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Results Section
const ResultsSection = () => {
  const stats = [
    { value: "Rs.10Cr+", label: "Ads Spent", color: "text-red-400" },
    { value: "70Cr+", label: "Revenue Generated", color: "neon-gold" },
    { value: "75,000+", label: "Leads Generated", color: "neon-cyan" },
    { value: "50+", label: "Brands Trusted", color: "text-green-400" }
  ];

  const testimonials = [
    {
      name: "Navdeep Singh Shekhawat",
      company: "DeshGhumo",
      quote: "Globewick transformed our lead generation completely. We went from struggling to get 10 leads a month to getting 50+ qualified inquiries weekly. Our revenue has grown 3x in just 6 months.",
      rating: 5
    },
    {
      name: "Guru Dutt",
      company: "Vrindavan Tours And Packages",
      quote: "The quality of leads we get now is incredible. These are people who are ready to book, not just window shoppers. Hritik and team understand the travel industry inside out.",
      rating: 5
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-[#0A1325]" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#00E5FF] uppercase tracking-[0.2em] font-bold text-sm mb-4">
            Proven Results
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Numbers That <span className="neon-gold">Speak</span> For Themselves
          </h2>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-20">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass-card rounded-2xl p-4 sm:p-8 text-center border border-white/5"
              data-testid={`stat-card-${index}`}
            >
              <div className={`font-black text-2xl sm:text-3xl lg:text-4xl ${stat.color} mb-2`}>{stat.value}</div>
              <p className="text-slate-400 text-xs sm:text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="testimonial-card"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex gap-1 mb-4 mt-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FFD700] text-[#FFD700]" />
                ))}
              </div>
              <p className="text-slate-300 leading-relaxed mb-6 italic">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#007AFF] flex items-center justify-center text-black font-bold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-slate-400 text-sm">{testimonial.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Discovery & Analysis",
      description: "We dive deep into your travel niche, understand your ideal customers, and analyze your competition."
    },
    {
      number: "02",
      title: "Campaign Launch",
      description: "We create and launch high-converting ad campaigns on Meta and Google tailored to your audience."
    },
    {
      number: "03",
      title: "Qualified Leads Daily",
      description: "Watch qualified leads flow into your inbox every day, ready to book their next adventure."
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-[#030712]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#00E5FF] uppercase tracking-[0.2em] font-bold text-sm mb-4">
            Our Process
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            How It <span className="neon-cyan">Works</span>
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
            A simple 3-step process to transform your travel agency's lead generation
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-1/3 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-[#00E5FF] via-[#FFD700] to-[#00E5FF]" />
          
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative z-10"
              data-testid={`process-step-${index}`}
            >
              <div className="glass-card rounded-2xl p-8 border border-white/5 text-center h-full">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#007AFF] flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-black text-black">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Section
const FounderSection = () => {
  return (
    <section className="py-20 lg:py-32 bg-[#0A1325]" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#00E5FF]/30">
              <img 
                src={FOUNDER_IMAGE}
                alt="Hritik Sharma - Founder of Globewick"
                className="w-full"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Hritik Sharma
            </h2>
            <p className="text-[#00E5FF] font-semibold mb-6">
              Founder & Lead Performance Marketer, Globewick
            </p>
            <p className="text-slate-300 leading-relaxed mb-8">
              Hritik is one of India's top travel and performance marketers - known for building, 
              scaling, and optimizing campaigns that generate real revenue, not vanity metrics. 
              Over the last 8 years, he's worked with 50+ national and international brands across 
              travel, healthcare, education, and e-commerce - managing ad spends exceeding Rs.10 Crores 
              and generating over 70Cr+ Revenue.
            </p>

            <div className="space-y-6 mb-8">
              <div>
                <div className="font-black text-2xl sm:text-3xl lg:text-4xl text-red-400 mb-1">Rs.10Cr+</div>
                <p className="text-slate-400 text-sm sm:text-base">Ads Spent - Scaled campaigns across Meta & Google with consistent, profitable ROAS.</p>
              </div>
              <div>
                <div className="font-black text-2xl sm:text-3xl lg:text-4xl neon-gold mb-1">70Cr+</div>
                <p className="text-slate-400 text-sm sm:text-base">Revenue Generated - Built data-driven funnels turning cold traffic into loyal customers.</p>
              </div>
              <div>
                <div className="font-black text-2xl sm:text-3xl lg:text-4xl neon-cyan mb-1">8+</div>
                <p className="text-slate-400 text-sm sm:text-base">Years Experience - Trusted by 50+ brands for high-impact, performance marketing systems.</p>
              </div>
            </div>

            <blockquote className="border-l-4 border-[#FFD700] pl-6 italic text-slate-300">
              "Marketing isn't about shouting louder - it's about understanding deeper. 
              I don't run ads. I build systems that scale."
              <footer className="mt-2 text-[#FFD700] font-semibold not-italic">- Hritik Sharma</footer>
            </blockquote>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Offer Section
const OfferSection = ({ scrollToForm }) => {
  return (
    <section className="py-20 lg:py-32 bg-[#030712] relative overflow-hidden" data-testid="offer-section">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00E5FF]/10 rounded-full blur-[100px]" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-card rounded-3xl p-8 lg:p-12 border border-[#00E5FF]/30 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-[#FFD700]/10 text-[#FFD700] px-4 py-2 rounded-full mb-6 font-semibold text-sm">
            <Award className="w-4 h-4" />
            Limited Time Offer
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Get <span className="neon-cyan">20-50 Qualified</span> Travel Leads in 30 Days
          </h2>

          <p className="text-xl text-slate-300 mb-8">
            Or <span className="neon-gold font-bold">We Work For Free</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>100% Risk-Free Guarantee</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>No Long-Term Contracts</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span>Results-Based Pricing</span>
            </div>
          </div>

          <Button 
            onClick={scrollToForm}
            className="btn-primary px-6 sm:px-12 py-4 sm:py-6 text-sm sm:text-lg rounded-none animate-pulse-glow w-full sm:w-auto"
            data-testid="offer-cta-button"
          >
            <span className="hidden sm:inline">Claim Your Free Strategy Session</span>
            <span className="sm:hidden">Get Free Strategy Session</span>
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
          </Button>

          <p className="text-slate-500 text-sm mt-6">
            Only accepting 5 new agencies this month
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Lead Capture Form
const LeadCaptureForm = ({ formRef }) => {
  const [formData, setFormData] = useState({
    name: "",
    agency_name: "",
    monthly_ad_budget: "",
    phone: "",
    whatsapp: "",
    business_type: "",
    business_description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, monthly_ad_budget: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        agency_name: "",
        monthly_ad_budget: "",
        phone: "",
        whatsapp: "",
        business_type: "",
        business_description: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={formRef}
      className="py-20 lg:py-32 bg-[#0A1325] form-section" 
      id="contact-form"
      data-testid="lead-form-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            We've spent <span className="neon-cyan">Rs.2Cr+</span> in learning so you don't have to!
          </h2>
          <p className="text-slate-400 text-lg">
            Your perfect partner to craft, optimize and grow your brand digitally.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="glass-card rounded-2xl p-8 lg:p-12 border border-white/10"
          data-testid="lead-capture-form"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label htmlFor="name" className="text-slate-300 mb-2 block">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-dark"
                data-testid="form-name-input"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                Phone <span className="text-red-400">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                placeholder="Contact Number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="input-dark"
                data-testid="form-phone-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="agency_name" className="text-slate-300 mb-2 block">
              Agency Name <span className="text-red-400">*</span>
            </Label>
            <Input
              id="agency_name"
              name="agency_name"
              placeholder="Your Travel Agency Name"
              value={formData.agency_name}
              onChange={handleChange}
              required
              className="input-dark"
              data-testid="form-agency-input"
            />
          </div>

          <div className="mb-6">
            <Label htmlFor="whatsapp" className="text-slate-300 mb-2 block">
              If Highly Interested Share Your Whatsapp Number, We Will Reach You Soon <span className="text-red-400">*</span>
            </Label>
            <Input
              id="whatsapp"
              name="whatsapp"
              placeholder="WhatsApp Number"
              value={formData.whatsapp}
              onChange={handleChange}
              className="input-dark"
              data-testid="form-whatsapp-input"
            />
          </div>

          <div className="mb-6">
            <Label htmlFor="monthly_ad_budget" className="text-slate-300 mb-2 block">
              Monthly Ad Budget <span className="text-red-400">*</span>
            </Label>
            <Select onValueChange={handleSelectChange} value={formData.monthly_ad_budget}>
              <SelectTrigger className="input-dark" data-testid="form-budget-select">
                <SelectValue placeholder="Select your budget range" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A1325] border-white/10">
                <SelectItem value="under-50k">Under Rs.50,000</SelectItem>
                <SelectItem value="50k-1lakh">Rs.50,000 - Rs.1 Lakh</SelectItem>
                <SelectItem value="1lakh-5lakh">Rs.1 Lakh - Rs.5 Lakh</SelectItem>
                <SelectItem value="5lakh-10lakh">Rs.5 Lakh - Rs.10 Lakh</SelectItem>
                <SelectItem value="above-10lakh">Above Rs.10 Lakh</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6">
            <Label htmlFor="business_description" className="text-slate-300 mb-2 block">
              Describe Your Business in 2-3 Lines <span className="text-red-400">*</span>
            </Label>
            <Textarea
              id="business_description"
              name="business_description"
              placeholder="Tell us about your travel business..."
              value={formData.business_description}
              onChange={handleChange}
              className="input-dark min-h-[120px]"
              data-testid="form-description-textarea"
            />
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full btn-primary py-6 text-lg rounded-md"
            data-testid="form-submit-button"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>

          <p className="text-center text-slate-500 text-sm mt-4">
            Only accepting 5 new agencies this month
          </p>
        </motion.form>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ scrollToForm }) => {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20Globewick%2C%20I%27m%20interested%20in%20generating%20more%20leads%20for%20my%20travel%20agency.`;

  return (
    <section className="py-20 lg:py-32 bg-[#030712]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Ready to Scale Your <span className="neon-cyan">Travel Agency</span>?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you generate consistent, high-quality leads 
            and grow your revenue to Rs.1Cr+ and beyond.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToForm}
              className="btn-primary px-8 py-6 text-lg rounded-none"
              data-testid="cta-book-call-button"
            >
              <Phone className="mr-2 w-5 h-5" />
              Book a Free Call
            </Button>
            <a 
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                className="btn-whatsapp px-8 py-6 text-lg rounded-full w-full"
                data-testid="cta-whatsapp-button"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Sticky CTA
const StickyCTA = ({ scrollToForm, visible, hideAtFooter }) => {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20Globewick%2C%20I%27m%20interested%20in%20generating%20more%20leads%20for%20my%20travel%20agency.`;

  return (
    <AnimatePresence>
      {visible && !hideAtFooter && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A1325]/95 backdrop-blur-xl border-t border-[#00E5FF]/30 p-4"
          data-testid="sticky-cta"
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white font-semibold text-sm sm:text-base">
              Get 20+ Qualified Leads in 30 Days - <span className="neon-gold">Risk Free</span>
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={scrollToForm}
                className="btn-primary px-6 py-3 text-sm rounded-none"
                data-testid="sticky-book-call-button"
              >
                Book Free Call
              </Button>
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="btn-whatsapp px-6 py-3 text-sm rounded-full"
                  data-testid="sticky-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Exit Intent Popup
const ExitIntentPopup = ({ isOpen, onClose, scrollToForm }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A1325] border border-[#00E5FF]/30 max-w-lg" data-testid="exit-popup">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Wait! Don't Miss Out on <span className="neon-cyan">Free Leads</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-center py-6">
          <p className="text-slate-300 mb-6">
            Before you go, claim your free strategy session and discover how we can 
            generate 20-50 qualified leads for your travel agency in just 30 days.
          </p>
          <Button 
            onClick={() => {
              scrollToForm();
              onClose();
            }}
            className="btn-primary px-8 py-4 text-lg rounded-none w-full"
            data-testid="exit-popup-cta"
          >
            Get My Free Strategy Session
          </Button>
          <button 
            onClick={onClose}
            className="text-slate-500 text-sm mt-4 hover:text-slate-300 transition-colors"
          >
            No thanks, I don't want more leads
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Footer
const Footer = ({ scrollToForm }) => {
  return (
    <footer className="py-16 bg-[#030712] border-t border-white/10" data-testid="footer" id="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src={GLOBEWICK_LOGO} alt="Globewick" className="h-16 w-auto" />
            </div>
            <p className="text-slate-400 leading-relaxed mb-6 max-w-md">
              We help travel agencies generate consistent, high-quality leads using paid ads (Meta + Google), 
              helping them scale to Rs.1Cr+ revenue.
            </p>
            <Button 
              onClick={scrollToForm}
              className="btn-primary px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base rounded-none w-full sm:w-auto"
              data-testid="footer-cta-button"
            >
              Get Your Free Strategy Call
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Our Services</h4>
            <ul className="space-y-3">
              <li 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-400 hover:text-[#00E5FF] transition-colors cursor-pointer"
              >
                SEO Services
              </li>
              <li 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-400 hover:text-[#00E5FF] transition-colors cursor-pointer"
              >
                Google Ads
              </li>
              <li 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-400 hover:text-[#00E5FF] transition-colors cursor-pointer"
              >
                Meta Ads
              </li>
              <li 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-400 hover:text-[#00E5FF] transition-colors cursor-pointer"
              >
                Web Development
              </li>
              <li 
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-400 hover:text-[#00E5FF] transition-colors cursor-pointer"
              >
                Lead Generation
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`mailto:${COMPANY_EMAIL}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-[#00E5FF] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>{COMPANY_EMAIL}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-[#25D366] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>+91-9509608121</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <MapPin className="w-5 h-5" />
                <span>{COMPANY_LOCATION}</span>
              </li>
            </ul>
            
            {/* Partner Badges */}
            <div className="flex items-center gap-4 mt-6">
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg">
                <img src={GOOGLE_LOGO} alt="Google" className="w-5 h-5 object-contain" />
                <span className="text-xs font-semibold text-slate-300">Google Partner</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg">
                <img src={META_LOGO} alt="Meta" className="w-5 h-5 object-contain" />
                <span className="text-xs font-semibold text-slate-300">Meta Partner</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} Globewick. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-slate-500 text-sm hover:text-slate-300 transition-colors cursor-pointer">Privacy Policy</span>
            <span className="text-slate-500 text-sm hover:text-slate-300 transition-colors cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [hideAtFooter, setHideAtFooter] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownExitPopup, setHasShownExitPopup] = useState(false);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
      
      // Check if we've reached the footer
      const footer = document.getElementById('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        // Hide sticky CTA when footer is visible (top of footer is in viewport)
        setHideAtFooter(footerRect.top < windowHeight);
      }
    };

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownExitPopup) {
        setShowExitPopup(true);
        setHasShownExitPopup(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [hasShownExitPopup]);

  return (
    <div className="min-h-screen bg-[#030712]">
      <Toaster position="top-center" richColors />
      
      <HeroSection scrollToForm={scrollToForm} />
      <ProblemSection />
      <SolutionSection />
      <ResultsSection />
      <ProcessSection />
      <FounderSection />
      <OfferSection scrollToForm={scrollToForm} />
      <LeadCaptureForm formRef={formRef} />
      <CTASection scrollToForm={scrollToForm} />
      <Footer scrollToForm={scrollToForm} />

      <StickyCTA scrollToForm={scrollToForm} visible={showStickyCTA} hideAtFooter={hideAtFooter} />
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)}
        scrollToForm={scrollToForm}
      />
    </div>
  );
}

export default App;
